/**
 * Feature: Storm Tracking & D2D Canvassing
 * /features/storm-canvassing
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  CloudRain,
  Map,
  Navigation,
  Smartphone,
  UserCheck,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'How does Rooflyzer detect storm events?',
    answer:
      'We integrate with NOAA weather data and hail reporting networks to detect events by zip code. You set the damage threshold (e.g., 1-inch hail) and we alert you automatically.',
  },
  {
    question: 'Can I use this for non-storm campaigns?',
    answer:
      'Yes. Territory management and canvassing tools work for any door-to-door campaign — storm recovery, seasonal campaigns, neighborhood blitzes.',
  },
  {
    question: 'Does the canvassing app work without cell signal?',
    answer:
      'Yes. The mobile canvassing app works fully offline. Routes, lead forms, and notes sync when your team reconnects to cell or WiFi.',
  },
  {
    question: 'Can I see where competitors are canvassing?',
    answer:
      "Not directly, but Rooflyzer's territory marking lets you flag 'covered' areas and see your own team's density — helping you avoid over-saturated blocks.",
  },
  {
    question: 'Is there a limit to how many canvassers I can deploy?',
    answer:
      'No. All users on Pro and Scale plans can use canvassing tools simultaneously. Each user has their own territory and lead log.',
  },
];

const stormCanvassingStats = [
  {
    value: 'Alert-first',
    label: 'Storm monitoring',
    desc: 'Know when an event touches your ZIPs',
  },
  {
    value: 'Mapped',
    label: 'Affected properties',
    desc: 'Ranked by damage probability',
  },
  {
    value: 'Territory',
    label: 'Assignments',
    desc: 'No rep knocking the same door twice',
  },
  {
    value: '15 min',
    label: 'From alert to knock list',
    desc: 'Routes, pins, and assignments ready',
  },
];

export const StormCanvassingPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Storm Tracking & D2D Canvassing',
    description:
      'Deploy your sales team the moment a storm hits. Rooflyzer tracks hail and wind events, maps affected properties, and routes canvassers to the highest-value addresses first.',
    featureList: [
      'Storm event alerts',
      'Property heat maps',
      'Optimized canvassing routes',
      'Mobile canvassing app',
      'Territory assignment',
      'Storm pipeline dashboard',
    ],
    url: 'https://rooflyzer.pro/features/storm-canvassing',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Storm Tracking & Door-to-Door Canvassing Software"
        description="Deploy your sales team the moment a storm hits. Rooflyzer tracks hail and wind events, maps affected properties, and routes canvassers to the highest-value addresses first."
        canonicalUrl="/features/storm-canvassing"
        ogImage="/og/canvassing.png"
        keywords="roofing storm tracking software, door-to-door canvassing app, roofing canvassing tool, storm damage lead generation"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Storm & Canvassing', url: '/features/storm-canvassing' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={CloudRain}>Own Your Territory</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              From Storm Alert to{' '}
              <span className="text-[#C29BF8]">Signed Contract in 48 Hours</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Rooflyzer monitors storm events in your market, maps every affected property, and
              routes your canvassing team to the highest-value doors first. While competitors are
              still planning, you're already signing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold px-8 shadow-md shadow-[#7950A2]/20"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Start Canvassing Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/features/ai-roofing-crm">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See Territory Maps
                </Button>
              </Link>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.canvassing} float={false} />
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Storm to Signature — Every Step Covered
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: CloudRain,
                  title: 'Storm Event Alerts',
                  desc: 'Get notified when a hail or wind event exceeds damage threshold in your territory. Includes storm date, size, and affected zip codes.',
                },
                {
                  icon: Map,
                  title: 'Property Heat Maps',
                  desc: 'Color-coded maps showing lead density, canvassing coverage, and close rates by zip code and neighborhood.',
                },
                {
                  icon: Navigation,
                  title: 'Optimized Canvassing Routes',
                  desc: 'Generate door-knock sequences that minimize drive time and maximize coverage in affected neighborhoods.',
                },
                {
                  icon: Smartphone,
                  title: 'Mobile Canvassing App',
                  desc: 'Your team logs every door — knocked, interested, scheduled, not home. Works offline in dead zones.',
                },
                {
                  icon: UserCheck,
                  title: 'Territory Assignment',
                  desc: "Assign zip codes or custom territories to individual reps. No stepping on each other's leads.",
                },
                {
                  icon: TrendingUp,
                  title: 'Storm Pipeline Dashboard',
                  desc: 'See total doors knocked, contact rate, inspection set rate, and close rate for every storm event.',
                },
              ]}
            />
          </section>

          {/* Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Hear about storms from neighbors or social media',
                    'Scramble to send teams without property data',
                    'Multiple reps knock the same doors',
                    'Lose track of who said what and when',
                    'Miss the best neighborhoods before competitors arrive',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Rooflyzer Way</h2>
                <FeatureChecklist
                  items={[
                    'Automated storm alerts the moment an event is detected',
                    'Mapped affected properties ranked by damage probability',
                    'Territory assignments prevent rep overlap',
                    'Mobile lead logging with every door outcome recorded',
                    'Real-time dashboard to track team performance per storm',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats title="What the storm workflow does" items={stormCanvassingStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* Bottom CTA */}
          <FeatureSignup
            title="Ready to Own Your Storm Market?"
            buttonLabel="Start Canvassing Free"
          >
            Be the first crew on the block after every storm. Free to start — no credit card
            required.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default StormCanvassingPage;
